import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import { getPdpSuggestion } from "../../../api/pdpApi";
import IconButtonComponent from "../../atoms/iconButton";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import CategorySuggestionBannerSkeleton from "./CategorySuggestionBannerSkeleton";
import RotatingBorderBox from "../../atoms/rotatingBorderBox";
import { setIsNavigatingAction } from "../../../store/slices/commonSlice";

/**
 * CategorySuggestionBanner component displays a banner with category suggestions.
 *
 * @param {Object} param0 - Component props
 * @param {boolean} param0.isMobile - Indicates if the view is mobile
 * @returns {JSX.Element|null} The rendered component or null if no suggestion is available
 */
const CategorySuggestionBanner = ({ isMobile, cleverTapPageData, pdpTopSuggestion }) => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const [suggestion, setSuggestion] = useState(null);
  const [isShowArrow, setIsShowArrow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const productName = useSelector((state) => state?.pdp?.fullProductDetails?.productName);

  useEffect(() => {
    /**
     * Fetches category suggestions based on the product name.
     * @returns {Promise<void>}
     */
    const fetchSuggestions = async () => {
      try {
        setIsLoading(true);
        const response = await getPdpSuggestion(productName);
        setSuggestion(response);
      } catch (error) {
        console.error("Error fetching category suggestions:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (productName) {
      fetchSuggestions();
    }
  }, [productName]);

  /**
   * Handles scroll event to toggle the visibility of the back arrow.
   */
  const handleScroll = () => {
    if (window.scrollY > 40) {
      setIsShowArrow(true);
    } else {
      setIsShowArrow(false);
    }
  };

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /**
   * Navigates back if referrer is from fnp domains, otherwise redirects to home.
   */
  const onHandleBack = () => {
    if (document.referrer && (document.referrer.includes("fnp.com") || document.referrer.includes("fnpplus.com"))) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };

  /** */
  const onClickSuggestionPdp = () => {
    dispatch(setIsNavigatingAction(true));
    trackEvent(cleverTapCommonEvents?.similarCollectionClicked, cleverTapPageData, ["clevertap"]);
  };

  /**
   *
   * @returns {JSX.Element} The suggestion section JSX
   */
  const renderSuggestionSection = () => (
    <a
      href={suggestion?.categoryUrl}
      className={`flex w-full overflow-hidden rounded-8`}
      onClick={() => onClickSuggestionPdp()}
    >
      <div
        className={`flex w-full items-center justify-between rounded-8 ${
          isMobile ? `${pdpTopSuggestion === "TG2" ? "bg-fnp-100" : "bg-white-900"} px-8 py-6` : "bg-fnp-50 p-8"
        } gap-3`}
      >
        <div className="flex items-center justify-center gap-12">
          <CustomImage
            src={suggestion?.categoryImageUrl}
            alt="Suggestion_pdp_image"
            width={40}
            height={40}
            borderRadius={4}
          />
          <div className="flex flex-col justify-start gap-4">
            <Typography variant="p" Tag="p" classes="text-14 inter text-fnp-500 capitalize font-600 leading-4">
              {suggestion?.categoryName}
            </Typography>
            {pdpTopSuggestion === "TG2" && (
              <Typography variant="p" Tag="p" classes="text-12 inter text-fnp-500 capitalize font-500 leading-4">
                {suggestion?.categoryTagLine}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center">
          <CustomImage src="/icons/arrow-right-fnp.svg" alt="Suggestion_pdp_image" width={16} height={16} />
        </div>
      </div>
    </a>
  );

  if (isLoading) {
    return (
      <div
        className={`flex items-center gap-2 ${isMobile ? "mx-16 my-10 rounded-8 bg-fnp-100" : ""} transition-all duration-300`}
      >
        <div
          className={`flex w-full items-center justify-between rounded-8 ${
            isMobile ? `${pdpTopSuggestion === "TG2" ? "bg-fnp-100" : "bg-white-900"} px-16 py-10` : "bg-fnp-50 p-8"
          } flex items-center gap-3`}
        >
          <CategorySuggestionBannerSkeleton isMobile={isMobile} />
        </div>
      </div>
    );
  } else {
    if (suggestion && suggestion.categoryName) {
      return (
        <div
          className={`flex items-center gap-2 ${isMobile ? "mx-8 my-8" : ""} ${
            isMobile ? "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]" : ""
          }`}
        >
          {/* Arrow Icon Button */}
          <div
            className={`flex-none ${
              isMobile ? "transform-gpu transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]" : ""
            } ${
              isMobile
                ? isShowArrow
                  ? "w-[40px] translate-x-0 opacity-100" // visible
                  : "pointer-events-none w-0 -translate-x-4 opacity-0" // hidden but smooth exit
                : "w-0"
            }`}
          >
            {isMobile && (
              <div className="animate-fadeInLeft flex-none pl-4" onClick={onHandleBack}>
                <IconButtonComponent
                  labelColor="w-full font-normal"
                  iconSrc="/icons/left-arrow.svg"
                  classes="py-2"
                  imgHeight={24}
                  imgWidth={24}
                  isPriority={false}
                />
              </div>
            )}
          </div>

          {/* Rotating Border Box */}
          <div
            className={`flex-1 ${
              isMobile ? "transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]" : ""
            } ${isMobile ? (isShowArrow ? "translate-x-0" : "-translate-x-2") : ""}`}
          >
            {pdpTopSuggestion === "TG2" ? (
              <RotatingBorderBox hasFade={true} borderColor="#7D8035">
                {renderSuggestionSection()}
              </RotatingBorderBox>
            ) : (
              renderSuggestionSection()
            )}
          </div>
        </div>
      );
    } else {
      return null;
    }
  }
};

CategorySuggestionBanner.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  cleverTapPageData: PropTypes.object,
  pdpTopSuggestion: PropTypes.string,
};

export default CategorySuggestionBanner;
